import { useSnackbarContext } from "@ameelio/ui";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { useApolloClient } from "@apollo/client/react";
import { Box } from "@mui/material";
import { Session } from "@ory/client-fetch";
import * as Sentry from "@sentry/react";
import { subMilliseconds } from "date-fns";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { track, unidentify } from "./analytics";
import { UserType } from "./api/graphql";
import RegistrationFailedScreen from "./Authentication/RegistrationFailedScreen";
import {
  CreateVisitorDocument,
  CreateVisitorMutationVariables,
} from "./CreateVisitor.generated";
import { ExtendSessionDocument } from "./ExtendSession.generated";
import { useAuthClient } from "./lib/auth-client";
import { BaseSkeleton } from "./lib/closet.tsx";
import useApolloErrorHandler from "./lib/handleApolloError";
import useBrowserLanguage from "./lib/useBrowserLanguage";
import { LoginDocument } from "./Login.generated";

export type AuthContextType = {
  isLoggedIn: boolean;
  login: (
    username: string,
    password: string,
    type: UserType
  ) => Promise<boolean>;
  token: string;
  orySession?: Session;
  register: (
    input: CreateVisitorMutationVariables["input"]
  ) => Promise<boolean>;
  logout: (tokenError?: boolean) => Promise<void>;
};

export const AuthContext = createContext<AuthContextType>(
  {} as AuthContextType
);

export function useAuthContext() {
  return useContext(AuthContext);
}

export default function AuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const handleApolloError = useApolloErrorHandler();
  const { t } = useTranslation();
  const { adoptBrowserLanguage } = useBrowserLanguage();
  const snackbarContext = useSnackbarContext();
  const becameInactiveAtRef = useRef<number | null>(null);
  const [tokenExpiresIn, setTokenExpiresIn] = useState<number | null>();
  const [extendedSessionCount, setExtendedSessionCount] = useState<number>(0);
  const client = useApolloClient();
  const {
    token,
    setToken: updateToken,
    orySession,
    loadingOrySession,
    isLoggedIn,
    registrationStatus,
    logout,
  } = useAuthClient({
    oryOrigin: import.meta.env.VITE_KRATOS_API_ORIGIN,
    logoutReturnTo: `${window.location.origin}/logged-out`,
    onError: (e) => Sentry.captureException(e),
    onMalanLogout: (tokenError) => {
      // Reset session extension variables
      becameInactiveAtRef.current = null;
      setTokenExpiresIn(null);
      // If the user was automatically logged out due to
      // a token error, give them feedback
      if (tokenError)
        snackbarContext.alert(
          "info",
          t("Your session expired. Please log in again.")
        );
      adoptBrowserLanguage();
      unidentify();
    },
  });

  // when the window loses visibility, mark the user as inactive. when
  // the window regains visibility, reset.
  useEffect(() => {
    const logActivityChange = () => {
      becameInactiveAtRef.current =
        window.document.visibilityState === "hidden"
          ? new Date().getTime()
          : null;
    };
    window.addEventListener("visibilitychange", logActivityChange);
    return () =>
      window.removeEventListener("visibilitychange", logActivityChange);
  }, []);

  // Set a timer to extend the session when it is close to expiring, but only if
  // the user remains active (window is visible) or became inactive "recently"
  // (we don't want to lose a session if someone looks away at the wrong moment).
  useEffect(() => {
    // If there is no token, the user is logged out and activity does not matter
    if (!token) return;
    // If there is no expiration time, the session is good forever and we do not
    // need to bother extending it.
    if (!tokenExpiresIn) return;

    const timeout = setTimeout(
      async () => {
        const secondsSinceInactivity = becameInactiveAtRef.current
          ? subMilliseconds(new Date(), becameInactiveAtRef.current).getTime() /
            1000
          : 0;
        // if the user only became inactive recently (relative to the total length
        // of the session) then we still want to extend the session.
        if (secondsSinceInactivity > tokenExpiresIn / 5) return;

        const response = await client.mutate({
          errorPolicy: "all", // return errors as data
          mutation: ExtendSessionDocument,
          context: {
            headers: {
              Authorization: token,
            },
          },
        });

        // Handle errors. If the token has already expired, fail silently and
        // allow the next authenticated request to force logout.
        if (response.error) {
          const errorMessage = CombinedGraphQLErrors.is(response.error)
            ? response.error.errors[0]?.message
            : response.error.message;
          const expectedErrors = [
            "API token is expired or revoked",
            "API token is invalid or missing",
            "Authentication is required to access this field",
          ];
          // Only report an unexpected error
          if (!expectedErrors.some((s) => errorMessage?.includes(s))) {
            Sentry.captureMessage(
              `Unable to extend session due to unexpected error: ${JSON.stringify(
                response.error
              )}`,
              "error"
            );
          }
          return;
        }

        // Update the expiresIn variable
        const { extendSession } = response.data || {};
        const newExpiresIn = extendSession?.remainingDuration;
        setTokenExpiresIn(newExpiresIn);
        if (extendSession) updateToken(extendSession.sessionId);

        // Update the call count to change the deps and force a re-execution
        // of the useEffect callback which should set a new timeout. We can't
        // use setInterval because remainingDuration may change, and we can't
        // rely on tokenExpiresIn changing because remainingDuration may not
        // change.
        setExtendedSessionCount((prev) => prev + 1);
      },

      // subtracting 10s to account for round-trip from this app -> Connect API -> Malan
      // (and back)
      Math.max(tokenExpiresIn * 1000 - 10000, 0)
    );

    return () => clearTimeout(timeout);
  }, [client, token, tokenExpiresIn, extendedSessionCount, updateToken]);

  const authContext = useMemo(
    (): AuthContextType => ({
      token,
      isLoggedIn,
      register: async (input): Promise<boolean> => {
        try {
          const result = await client.mutate({
            fetchPolicy: "no-cache",
            mutation: CreateVisitorDocument,
            variables: { input },
          });
          if (!result.data) throw new Error("Signup failed without data");
          updateToken(result.data.createVisitor.authToken);
          // Reset session extension variables
          becameInactiveAtRef.current = null;
          setTokenExpiresIn(null);
          setExtendedSessionCount(0);
          track("Signup - Success");
          return true;
        } catch (e) {
          handleApolloError(e);
          track("Signup - Failure", {
            reason: e instanceof Error ? e.message : "unknown",
          });
          return false;
        }
      },
      orySession,
      login: async (
        username: string,
        password: string,
        userType
      ): Promise<boolean> => {
        try {
          const result = await client.mutate({
            mutation: LoginDocument,
            variables: {
              input: {
                username,
                password,
                userType,
              },
            },
          });
          if (result.error || !result.data?.login?.sessionId) {
            const errorMessage =
              (CombinedGraphQLErrors.is(result.error)
                ? result.error.errors[0]?.message
                : result.error?.message) ||
              "Login failed without error message";
            throw new Error(errorMessage);
          }
          // Set the token and when it expires
          updateToken(result.data.login.sessionId);
          // Reset session extension variables
          becameInactiveAtRef.current = null;
          setTokenExpiresIn(result.data.login.remainingDuration);
          setExtendedSessionCount(0);
          track("Login - Success");
          return true;
        } catch (e) {
          handleApolloError(e);
          track("Login - Failure", {
            reason: e instanceof Error ? e.message : "unknown",
          });
          return false;
        }
      },
      logout,
    }),
    [
      client,
      token,
      isLoggedIn,
      handleApolloError,
      updateToken,
      logout,
      orySession,
    ]
  );

  if (loadingOrySession || registrationStatus === "pending")
    return (
      <Box style={{ height: "100vh" }}>
        <BaseSkeleton />
      </Box>
    );

  // Checked after loading: registration can fail before the Ory logout URL
  // is ready, and this screen's only way out is that logout.
  if (registrationStatus === "failed")
    return <RegistrationFailedScreen logout={() => void logout()} />;

  return (
    <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>
  );
}
